import { loginSchema, type LoginInput } from '@ruby/shared';
import { Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowRight, ChartColumn, ClipboardList, Eye, EyeOff, Factory, History, KeyRound, LockKeyhole, Package, ReceiptText, ShieldCheck, Smartphone, User } from 'lucide-react';
import { useState } from 'react';
import { AppForm, Field, SubmitButton, useAppForm } from '@/components/form/form';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api';
import { login } from '@/lib/auth';
import { useSession } from '@/lib/session';
import { BrandMark } from '../shell/brand';
import { SplashScreen } from '../shell/status-pages';
import { LoginArt } from './login-art';

/** What the system covers, shown on the brand panel. */
const FEATURES = [
  { icon: ClipboardList, title: 'Sales & Orders', text: 'Quotations, orders and customer credit' },
  { icon: Factory, title: 'Production', text: 'Jobs, material plans and work centres' },
  { icon: Package, title: 'Purchase & Stock', text: 'Requisitions, orders and warehouses' },
  { icon: ChartColumn, title: 'Accounts & HR', text: 'Ledger, VAT, payroll and attendance' },
];

const CHIPS = [
  { icon: ShieldCheck, label: 'Role-based access' },
  { icon: History, label: 'Full audit trail' },
  { icon: ReceiptText, label: 'VAT-ready invoices' },
];

/** "Remember my username": only the username is kept, in this browser. */
const REMEMBER_KEY = 'rf.login.username';
function rememberedUsername() {
  try { return localStorage.getItem(REMEMBER_KEY) ?? ''; } catch { return ''; }
}
function setRememberedUsername(name: string | null) {
  try { if (name) localStorage.setItem(REMEMBER_KEY, name); else localStorage.removeItem(REMEMBER_KEY); } catch { /* storage blocked */ }
}

export function LoginPage() {
  const status = useSession((s) => s.status);
  const { redirect } = useSearch({ from: '/login' });
  const navigate = useNavigate();
  const [saved] = useState(rememberedUsername);
  const [remember, setRemember] = useState(true);
  const form = useAppForm(loginSchema, { username: saved, password: '' });
  const [show, setShow] = useState(false);
  const [caps, setCaps] = useState(false);
  // Two-step sign-in: the server asks for the authenticator code after a right password.
  const [needCode, setNeedCode] = useState(false);
  const year = new Date().getFullYear();

  if (status === 'loading') return <SplashScreen />;
  if (status === 'authenticated' && !form.formState.isSubmitting) return <Navigate to={redirect ?? '/'} replace />;

  return (
    <div className="flex min-h-screen bg-[#f4faf7]">
      {/* Brand story */}
      <aside className="hidden w-[44%] max-w-[760px] flex-col items-center gap-9 bg-[radial-gradient(120%_80%_at_20%_0%,#ffffff_0%,#eef8f3_60%,#e3f3eb_100%)] px-12 pt-14 pb-10 text-[#0e2a1f] xl:flex">
        <div className="flex flex-col items-center gap-3.5">
          <BrandMark className="size-24 rounded-[28px] shadow-[0_10px_24px_-10px_rgba(11,59,40,0.5)]" />
          <p className="text-[60px] leading-none font-extrabold tracking-[-0.04em] text-[#0b3b28]">
            Ruby <span className="text-[#17905d]">Foam</span>
          </p>
          <div className="flex w-full max-w-[420px] items-center gap-4">
            <span className="h-0.5 flex-1 bg-[#17905d]/60" />
            <span className="pl-[0.42em] text-[17px] font-semibold tracking-[0.42em] text-[#0b3b28]">ENTERPRISE&nbsp;ERP</span>
            <span className="h-0.5 flex-1 bg-[#17905d]/60" />
          </div>
        </div>

        <div className="flex max-w-[600px] flex-col items-center gap-4 text-center">
          <h2 className="text-[38px] leading-[1.15] font-extrabold tracking-[-0.03em] text-[#0b2b1e]">
            From customer order to invoice,{' '}
            <span className="text-[#17905d] underline decoration-[#f2b33d] decoration-[5px] underline-offset-8">in one place.</span>
          </h2>
          <p className="text-[17px] leading-relaxed text-[#44574e]">
            Sales, production, stock, delivery, accounts and payroll — one connected system for furniture and mattress manufacturing.
          </p>
        </div>

        <ul className="grid w-full max-w-[740px] grid-cols-4">
          {FEATURES.map(({ icon: Icon, title, text }, i) => (
            <li key={title} className={`flex flex-col items-center gap-2.5 px-3 text-center ${i ? 'border-l border-[#cfe3d9]' : ''}`}>
              <span className="grid size-[52px] place-items-center rounded-full bg-white text-[#17905d] shadow-[0_4px_14px_rgba(14,80,52,0.12)]">
                <Icon className="size-6" strokeWidth={1.8} />
              </span>
              <span className="text-[15px] font-bold text-[#0b2b1e]">{title}</span>
              <span className="text-[13px] leading-snug text-[#52645b]">{text}</span>
            </li>
          ))}
        </ul>

        <div className="mt-auto flex w-full max-w-[760px] flex-wrap items-center gap-6 rounded-[22px] bg-[linear-gradient(115deg,#0b3b28_0%,#126a47_70%,#17905d_100%)] px-6 py-5 text-white shadow-[0_18px_40px_-18px_rgba(11,59,40,0.55)]">
          <div className="flex h-[88px] w-[108px] flex-col items-center justify-center rounded-2xl border border-white/20 bg-white/[0.08]">
            <span className="text-xs font-bold tracking-[0.18em] text-[#bfe8d3]">ONE</span>
            <span className="text-[28px] font-extrabold tracking-[-0.03em]">record</span>
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <p className="text-[21px] font-extrabold tracking-[-0.02em]">Everything connected</p>
            <div className="flex flex-wrap gap-2">
              {CHIPS.map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[13px] font-semibold">
                  <Icon className="size-3.5" /> {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </aside>

      {/* Artwork + sign-in card */}
      <main className="relative flex min-h-screen flex-1 items-center justify-center overflow-hidden px-4 py-10 sm:px-10 lg:justify-end lg:pr-12">
        <LoginArt className="pointer-events-none absolute inset-0 size-full" />

        <div className="relative flex w-full max-w-[480px] flex-col gap-5 rounded-[28px] bg-surface px-6 pt-10 pb-7 shadow-[0_30px_70px_-28px_rgba(11,59,40,0.45),0_2px_6px_rgba(11,59,40,0.06)] sm:px-12">
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex items-center gap-2.5">
              <BrandMark className="size-10" />
              <span className="text-[24px] font-extrabold tracking-[-0.03em] text-fg">
                Ruby<span className="text-[#17905d]">Foam</span>
              </span>
            </div>
            <h1 className="mt-1 text-[34px] leading-tight font-extrabold tracking-[-0.035em] text-fg">Welcome back</h1>
            <p className="text-[15px] leading-relaxed text-fg-muted">
              Sign in to your Ruby Foam ERP account.
              <br />
              Orders, production, stock and accounts — in one place.
            </p>
          </div>

          <AppForm
            form={form}
            guardUnsaved={false}
            errorTitle="Sign-in failed"
            submitOnLastEnter
            className="flex flex-col gap-5"
            onSubmit={async (v: LoginInput) => {
              try {
                await login(needCode ? v : { username: v.username, password: v.password });
              } catch (e) {
                if (e instanceof ApiError && e.code === 'MFA_REQUIRED') {
                  setNeedCode(true);
                  setTimeout(() => form.setFocus('code'), 50);
                  return;
                }
                throw e;
              }
              setRememberedUsername(remember ? v.username.trim() : null);
              await navigate({ to: redirect ?? '/', replace: true });
            }}
          >
            <Field name="username" label="Username or email" required>
              {(p) => (
                <div className="relative">
                  <User aria-hidden className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 text-fg-subtle" />
                  <Input {...p} {...form.register('username')} autoComplete="username" autoFocus={!saved} placeholder="e.g. ahmed.khan" className="h-[52px] rounded-xl pl-11 text-[15px]" />
                </div>
              )}
            </Field>
            <Field
              name="password"
              label="Password"
              required
              hint={caps ? <span className="flex items-center gap-1 font-medium text-warning"><KeyRound className="size-3.5" /> Caps Lock is on</span> : undefined}
            >
              {(p) => (
                <div className="relative">
                  <LockKeyhole aria-hidden className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 text-fg-subtle" />
                  <Input
                    {...p}
                    {...form.register('password')}
                    type={show ? 'text' : 'password'}
                    autoComplete="current-password"
                    autoFocus={!!saved}
                    placeholder="Enter your password"
                    className="h-[52px] rounded-xl pr-12 pl-11 text-[15px]"
                    onKeyUp={(e) => setCaps(e.getModifierState('CapsLock'))}
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute top-1/2 right-1.5 grid size-10 -translate-y-1/2 place-items-center rounded-lg text-fg-subtle hover:bg-surface-2 hover:text-fg"
                    aria-label={show ? 'Hide password' : 'Show password'}
                    aria-pressed={show}
                  >
                    {show ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
                  </button>
                </div>
              )}
            </Field>
            {needCode && (
              <Field name="code" label="Authenticator code" required hint="Open your authenticator app and type the 6-digit code for Ruby Foam ERP.">
                {(p) => (
                  <div className="relative animate-in">
                    <Smartphone aria-hidden className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 text-fg-subtle" />
                    <Input {...p} {...form.register('code')} inputMode="numeric" autoComplete="one-time-code" maxLength={7} placeholder="123 456" className="h-[52px] rounded-xl pl-11 text-[17px] tracking-[0.3em] tabular-nums" />
                  </div>
                )}
              </Field>
            )}
            <label className="flex cursor-pointer items-center gap-3 text-sm font-medium text-fg">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="size-5 accent-[#17905d]" />
              Remember my username on this device
            </label>
            <SubmitButton size="lg" className="group h-[54px] w-full rounded-[14px] bg-[linear-gradient(180deg,#1b9d66_0%,#12784d_100%)] text-base font-bold shadow-[0_10px_22px_-10px_rgba(18,120,77,0.7)]">
              {needCode ? 'Verify & sign in' : 'Sign in'} <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
            </SubmitButton>
          </AppForm>

          <p className="text-center text-[13px] text-fg-muted">
            <KeyRound className="-mt-0.5 mr-1.5 inline size-4 text-fg-subtle" />
            Locked out? Your system administrator can reset your password.
          </p>
          <p className="border-t border-border pt-4 text-center text-xs leading-relaxed text-fg-subtle">
            © {year} Ruby Foam · United Arab Emirates
            <br />
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="size-3.5" /> Authorised users only. Activity is recorded.</span>
          </p>
        </div>
      </main>
    </div>
  );
}
